export interface ApiUser {
  id: string
  email: string
  displayName: string
  role: 'admin' | 'user'
  createdAt: string
}

export interface RootSummary {
  id: string
  kind: 'node' | 'share'
  label: string
  nodeName: string
  permission: 'read' | 'write'
  online: boolean
  mount: string | null
  diskFreeBytes: number | null
  ephemeralRoot: boolean
  quota: {
    usedBytes: number
    limitBytes: number | null
    nodeUsedBytes: number
    nodeLimitBytes: number | null
  }
}

export interface FsEntry {
  name: string
  type: 'file' | 'dir'
  size: number
  mtime: number
}

export interface ListResult {
  path: string
  permission: 'read' | 'write'
  entries: FsEntry[]
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * The access token is kept in memory only. Persistence comes from the httpOnly
 * refresh cookie, so a script that manages to run on the page still cannot read
 * a long-lived credential out of localStorage.
 */
let accessToken: string | null = null
let refreshInFlight: Promise<string | null> | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

async function parseError(response: Response): Promise<never> {
  let message = `request failed with ${response.status}`
  try {
    const body = (await response.json()) as { message?: string }
    if (body.message) message = body.message
  } catch {
    // Non-JSON error body; the status-based message is the best we have.
  }
  throw new ApiError(response.status, message)
}

/** Exchanges the refresh cookie for a new access token, coalescing concurrent calls. */
export function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', { method: 'POST' })
      if (!response.ok) return null
      const body = (await response.json()) as { accessToken: string }
      accessToken = body.accessToken
      return body.accessToken
    } catch {
      return null
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/** 인증을 붙여 보낸다. 만료된 토큰은 한 번 새로 받아 조용히 다시 보낸다. */
async function authorized(path: string, init: RequestInit = {}): Promise<Response> {
  const send = async (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers)
    if (token) headers.set('authorization', `Bearer ${token}`)
    if (init.body !== undefined && !headers.has('content-type')) {
      headers.set('content-type', 'application/json')
    }
    return fetch(path, { ...init, headers })
  }

  const response = await send(accessToken)
  // Access tokens are short-lived by design; refresh once and retry silently.
  if (response.status !== 401) return response

  const token = await refreshAccessToken()
  if (!token) throw new ApiError(401, 'session expired')
  return send(token)
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await authorized(path, init)
  if (!response.ok) await parseError(response)
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}

/**
 * 한 줄에 JSON 하나씩 오는 응답을 오는 대로 읽는다.
 *
 * 오래 걸리는 일의 진행을 보여주려면 끝나기 전에 뭔가 와야 한다. 첫 줄이 나간 뒤에는
 * 상태 코드를 바꿀 수 없으므로, 실패도 본문 안에서 말한다.
 */
async function* ndjson(response: Response): AsyncGenerator<unknown> {
  if (!response.body) return
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffered = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffered += decoder.decode(value, { stream: true })

    // 마지막 조각은 아직 줄이 다 오지 않았을 수 있으므로 남겨둔다.
    const lines = buffered.split('\n')
    buffered = lines.pop() ?? ''
    for (const line of lines) if (line !== '') yield JSON.parse(line) as unknown
  }

  if (buffered.trim() !== '') yield JSON.parse(buffered) as unknown
}

// ---- auth ----

export async function login(email: string, password: string) {
  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!response.ok) await parseError(response)
  const body = (await response.json()) as { accessToken: string; user: ApiUser }
  accessToken = body.accessToken
  return body.user
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' })
  accessToken = null
}

export function fetchMe() {
  return api<{ user: ApiUser }>('/api/auth/me').then((r) => r.user)
}

export function changePassword(currentPassword: string, newPassword: string) {
  return api<{ ok: true }>('/api/auth/password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  })
}

export interface AppConfig {
  /**
   * The separate origin file bytes come from, or null on a single-host
   * deployment. When it is set, every file type can be opened in a tab: the
   * content lands in an origin that holds none of this app's session.
   */
  contentOrigin: string | null
}

export function fetchConfig() {
  return api<AppConfig>('/api/config')
}

// ---- roots and files ----

export function fetchRoots() {
  return api<{ roots: RootSummary[] }>('/api/roots').then((r) => r.roots)
}

export function listFolder(rootId: string, path: string) {
  const query = new URLSearchParams({ path })
  return api<ListResult>(`/api/roots/${encodeURIComponent(rootId)}/list?${query}`)
}

export interface StatEntry {
  path: string
  exists: boolean
  type: 'file' | 'dir' | null
  size: number
  mtime: number
}

/**
 * 허브가 한 요청에 받아주는 경로 수. 폴더를 통째로 올리면 사진 폴더 하나로도 넘어가므로
 * 여기서 잘라 보낸다 — 상한은 허브 사정이지, 부르는 쪽이 알 일이 아니다.
 */
const STAT_BATCH = 500

/** Checks a batch of destination paths so conflicts can be shown in one dialog. */
export async function statPaths(rootId: string, paths: string[]): Promise<StatEntry[]> {
  const entries: StatEntry[] = []

  for (let start = 0; start < paths.length; start += STAT_BATCH) {
    const batch = await api<{ entries: StatEntry[] }>(
      `/api/roots/${encodeURIComponent(rootId)}/stat`,
      { method: 'POST', body: JSON.stringify({ paths: paths.slice(start, start + STAT_BATCH) }) },
    )
    entries.push(...batch.entries)
  }

  return entries
}

export function createFolder(rootId: string, path: string) {
  return api<{ ok: true }>(`/api/roots/${encodeURIComponent(rootId)}/mkdir`, {
    method: 'POST',
    body: JSON.stringify({ path }),
  })
}

export function renameEntry(rootId: string, from: string, to: string) {
  return api<{ ok: true }>(`/api/roots/${encodeURIComponent(rootId)}/rename`, {
    method: 'POST',
    body: JSON.stringify({ from, to }),
  })
}

/** 지운 것 하나하나. 되돌리려면 이 id 가 필요하다. */
export interface TrashedItem {
  id: string
  name: string
}

/** 지우지 않고 노드의 휴지통으로 보낸다. 되돌릴 수 있다는 뜻이다. */
/**
 * 복사한 결과 어디에 놓였는지를 돌려준다 — 이름이 겹치면 `사진 (2).jpg` 가 되므로,
 * 부른 쪽이 정한 경로와 다를 수 있다. 되돌리려면 실제로 생긴 그 경로가 필요하다.
 */
export function copyEntry(rootId: string, from: string, to: string, onConflict: 'fail' | 'rename') {
  return api<{ ok: true; path: string; renamed: boolean }>(
    `/api/roots/${encodeURIComponent(rootId)}/copy`,
    { method: 'POST', body: JSON.stringify({ from, to, onConflict }) },
  )
}

export interface TransferResult {
  arrived: string[]
  filesSent: number
  failed: string[]
  bytes: number
  trashedOriginals: boolean
  /** 도중에 멈춘 것. 도착한 것까지는 그대로 있다. */
  cancelled: boolean
  /** 반쯤 건너간 채로 멈춘 폴더. 양쪽 어느 쪽을 봐도 알 수 없으므로 말해줘야 한다. */
  partial: string[]
}

export interface TransferProgress {
  files: number
  bytes: number
  current: string
}

/**
 * 다른 root 로 옮기거나 복사한다. 두 root 가 다른 기계에 있어도 된다 — 그때는 바이트가
 * 허브를 거쳐 흐른다. 목적지가 URL 인 이유는, 쓸 수 있는지와 용량이 남았는지를 정하는
 * 쪽이 거기이기 때문이다.
 */
export async function transferEntries(
  options: {
    toRootId: string
    fromRootId: string
    paths: string[]
    into: string
    mode: 'move' | 'copy'
  },
  onProgress?: (progress: TransferProgress) => void,
  /**
   * 허브가 이 일에 붙인 이름표. 멈추려면 이것이 필요하고, 첫 줄로 온다 — 브라우저가
   * 이 응답을 끊어도 허브는 하던 것을 마저 하므로, 멈추라는 말은 다른 요청으로 가야
   * 한다.
   */
  onStarted?: (transferId: string) => void,
): Promise<TransferResult> {
  const { toRootId, ...body } = options
  const response = await authorized(`/api/roots/${encodeURIComponent(toRootId)}/transfer`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!response.ok) await parseError(response)

  let result: TransferResult | null = null
  for await (const line of ndjson(response)) {
    const message = line as { type?: string; message?: string; id?: string } &
      Partial<TransferProgress> &
      Partial<TransferResult>
    if (message.type === 'started') {
      if (message.id) onStarted?.(message.id)
    } else if (message.type === 'progress') {
      onProgress?.({
        files: message.files ?? 0,
        bytes: message.bytes ?? 0,
        current: message.current ?? '',
      })
    } else if (message.type === 'error') {
      throw new ApiError(500, message.message ?? 'transfer failed')
    } else if (message.type === 'done') {
      result = message as unknown as TransferResult
    }
  }

  // 끝났다는 줄이 없으면 연결이 도중에 끊긴 것이다. 절반쯤 된 것을 성공이라 하지 않는다.
  if (result === null) throw new ApiError(500, '전송이 끝나기 전에 연결이 끊겼습니다')
  return result
}

/** 도는 것을 멈춘다. 이미 건너간 것은 그대로 둔다. */
export function cancelTransfer(rootId: string, transferId: string) {
  return api<{ ok: true }>(
    `/api/roots/${encodeURIComponent(rootId)}/transfer/${encodeURIComponent(transferId)}/cancel`,
    { method: 'POST' },
  )
}

/**
 * 한 폴더의 썸네일을 부를 수 있는 이름표.
 *
 * 격자 하나가 사진 백 장을 한꺼번에 청하므로 파일마다 티켓을 받으면 첫 장이 뜨기 전에
 * 왕복이 백 번이다. 그래서 티켓은 폴더 하나를 가리키고, 주소에 이름을 붙여 쓴다.
 */
export interface ThumbnailTicket {
  ticket: string
  /** 바이트가 나오는 자리. 콘텐츠 호스트가 따로 있으면 그쪽이다. */
  base: string
}

export function requestThumbnailTicket(rootId: string, path: string) {
  return api<ThumbnailTicket>(`/api/roots/${encodeURIComponent(rootId)}/tickets/thumbnails`, {
    method: 'POST',
    body: JSON.stringify({ path }),
  })
}

export function thumbnailUrl(ticket: ThumbnailTicket, name: string): string {
  const params = new URLSearchParams({ ticket: ticket.ticket, name })
  return `${ticket.base}?${params.toString()}`
}

export interface SearchHit {
  /** 이 root 기준 경로. */
  path: string
  name: string
  type: 'file' | 'dir'
  size: number
  mtime: number
}

export interface SearchOutcome {
  /** 몇 개를 찾았는지. */
  found: number
  /** 상한에 걸려 멈춘 이유. 끝까지 걸었으면 null. */
  stopped: 'limit' | 'time' | null
}

/**
 * 이름으로 찾는다. 찾는 대로 한 건씩 흘러나오므로, 큰 트리에서도 첫 결과가 곧 보인다.
 *
 * `signal` 로 끊으면 허브가 에이전트의 걷기를 그 자리에서 멈춘다 — 화면을 떠나거나
 * 다른 말을 쳐 넣었을 때, 앞의 검색이 디스크를 계속 걷고 있을 이유가 없다.
 */
export async function searchEntries(
  rootId: string,
  path: string,
  q: string,
  onHit: (hit: SearchHit) => void,
  signal?: AbortSignal,
): Promise<SearchOutcome> {
  const params = new URLSearchParams({ path, q })
  const response = await authorized(
    `/api/roots/${encodeURIComponent(rootId)}/search?${params.toString()}`,
    { signal },
  )
  if (!response.ok) await parseError(response)

  let outcome: SearchOutcome | null = null
  for await (const line of ndjson(response)) {
    const message = line as { type?: string; hit?: SearchHit } & Partial<SearchOutcome>
    if (message.type === 'hit' && message.hit) onHit(message.hit)
    else if (message.type === 'done') {
      outcome = { found: message.found ?? 0, stopped: message.stopped ?? null }
    }
  }

  // 끝났다는 줄이 없으면 도중에 끊긴 것이다. 절반쯤 찾은 것을 다 찾았다고 하지 않는다.
  if (outcome === null) throw new ApiError(500, '찾는 중에 연결이 끊겼습니다')
  return outcome
}

export function deleteEntries(rootId: string, paths: string[]) {
  return api<{ ok: true; trashed: TrashedItem[] }>(
    `/api/roots/${encodeURIComponent(rootId)}/delete`,
    { method: 'POST', body: JSON.stringify({ paths }) },
  )
}

/** 지운 것 한 줄. `path` 는 지우기 전에 있던 자리, 이 root 기준이다. */
export interface TrashEntry {
  id: string
  path: string
  name: string
  deletedAt: number
  size: number
  type: 'file' | 'dir'
}

export function listTrash(rootId: string) {
  return api<{ items: TrashEntry[] }>(`/api/roots/${encodeURIComponent(rootId)}/trash`)
}

export function restoreEntries(rootId: string, ids: string[]) {
  return api<{ ok: true }>(`/api/roots/${encodeURIComponent(rootId)}/restore`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
}

/** 보관 기간을 기다리지 않고 지금 비운다. 이것만은 되돌릴 수 없다. */
export function purgeTrash(rootId: string, ids: string[]) {
  return api<{ ok: true }>(`/api/roots/${encodeURIComponent(rootId)}/trash/purge`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
}

/**
 * Downloads go through a short-lived ticket, so the URL carries no lasting
 * credential. It is still a credential while it lives: the ticket is replayable
 * for its two minutes, deliberately, so a paused or resumed transfer does not
 * find the link already spent. It covers one path and re-checks the account's
 * current access every time it is used.
 *
 * `inline` asks the hub to serve the file for viewing rather than saving. It is
 * a request, not a guarantee: the hub only honours it for types it considers
 * safe to render, and sends the rest as a download regardless.
 */
export async function downloadUrl(
  rootId: string,
  path: string,
  options: { inline?: boolean } = {},
): Promise<string> {
  const result = await api<{ url: string }>(
    `/api/roots/${encodeURIComponent(rootId)}/tickets/download`,
    { method: 'POST', body: JSON.stringify({ path, inline: options.inline ?? false }) },
  )
  return result.url
}

/** Asks the hub to park a selection and hand back a plain ZIP download URL. */
export async function archiveUrl(rootId: string, paths: string[]): Promise<string> {
  const result = await api<{ url: string }>(
    `/api/roots/${encodeURIComponent(rootId)}/tickets/archive`,
    { method: 'POST', body: JSON.stringify({ paths }) },
  )
  return result.url
}

export interface UploadHandle {
  promise: Promise<void>
  abort: () => void
}

export type ConflictPolicy = 'fail' | 'overwrite' | 'rename'

/** Where one upload is going, and what it needs to be let in. */
interface UploadTarget {
  url: string
  headers: Record<string, string>
}

/**
 * Uploads one file with progress. XHR rather than fetch: it is still the only
 * way to observe request-body progress in every browser we care about.
 *
 * `resolveTarget` runs immediately before the request rather than at call time,
 * because what it produces expires — an access token, a two-minute ticket — and
 * a file can sit in a picker for a while before anyone presses the button.
 */
function xhrUpload(
  file: File,
  onProgress: (loaded: number, total: number) => void,
  resolveTarget: () => Promise<UploadTarget>,
): UploadHandle {
  const xhr = new XMLHttpRequest()

  const promise = new Promise<void>((resolve, reject) => {
    void (async () => {
      let target: UploadTarget
      try {
        target = await resolveTarget()
      } catch (err) {
        reject(err instanceof Error ? err : new ApiError(0, String(err)))
        return
      }

      xhr.open('PUT', target.url)
      for (const [name, value] of Object.entries(target.headers)) {
        xhr.setRequestHeader(name, value)
      }

      xhr.upload.addEventListener('progress', (event) => {
        onProgress(event.loaded, event.total || file.size)
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(file.size, file.size)
          resolve()
          return
        }
        let message = `upload failed with ${xhr.status}`
        try {
          const body = JSON.parse(xhr.responseText) as { message?: string }
          if (body.message) message = body.message
        } catch {
          // Keep the status-based message.
        }
        reject(new ApiError(xhr.status, message))
      })

      xhr.addEventListener('error', () => reject(new ApiError(0, 'network error during upload')))
      xhr.addEventListener('abort', () => reject(new ApiError(0, 'upload cancelled')))

      xhr.send(file)
    })()
  })

  return { promise, abort: () => xhr.abort() }
}

export function uploadFile(options: {
  rootId: string
  path: string
  file: File
  onConflict: ConflictPolicy
  onProgress: (loaded: number, total: number) => void
}): UploadHandle {
  return xhrUpload(options.file, options.onProgress, async () => {
    // Make sure the token is fresh: an XHR cannot retry itself after a 401
    // without re-reading the whole file.
    const token = getAccessToken() ?? (await refreshAccessToken())
    if (!token) throw new ApiError(401, 'session expired')

    const query = new URLSearchParams({ path: options.path, onConflict: options.onConflict })
    return {
      url: `/api/roots/${encodeURIComponent(options.rootId)}/upload?${query}`,
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/octet-stream' },
    }
  })
}

/** The same upload, by someone with no account at all: the link is the credential. */
export function uploadToPublicLink(options: {
  token: string
  password?: string
  file: File
  onProgress: (loaded: number, total: number) => void
}): UploadHandle {
  return xhrUpload(options.file, options.onProgress, async () => {
    const url = await publicTicketUrl(options.token, options.password)
    return {
      url: `${url}&name=${encodeURIComponent(options.file.name)}`,
      headers: { 'content-type': 'application/octet-stream' },
    }
  })
}

// ---- public share links ----

export type LinkKind = 'download' | 'upload'

export interface ShareLink {
  id: string
  url: string
  kind: LinkKind
  filename: string
  path: string
  rootId: string
  hasPassword: boolean
  expiresAt: string | null
  maxUses: number | null
  useCount: number
  lastUsedAt: string | null
  revokedAt: string | null
  createdAt: string
}

export const links = {
  list: () => api<{ links: ShareLink[] }>('/api/links').then((r) => r.links),
  create: (body: {
    rootId: string
    path: string
    kind: LinkKind
    expiresInHours: number | null
    password: string | null
    maxUses: number | null
  }) =>
    api<{ link: ShareLink }>('/api/links', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.link),
  revoke: (id: string) => api<{ ok: true }>(`/api/links/${id}`, { method: 'DELETE' }),
}

export interface PublicLinkInfo {
  kind: LinkKind
  filename: string
  hasPassword: boolean
  expiresAt: string | null
  usesLeft: number | null
}

/** Anonymous: these two never carry an access token. */
export async function fetchPublicLink(token: string): Promise<PublicLinkInfo> {
  const response = await fetch(`/api/public/${encodeURIComponent(token)}`)
  if (!response.ok) await parseError(response)
  return (await response.json()) as PublicLinkInfo
}

/** Checks the password, if there is one, and hands back a short-lived URL to use. */
export async function publicTicketUrl(token: string, password?: string): Promise<string> {
  const response = await fetch(`/api/public/${encodeURIComponent(token)}/ticket`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  if (!response.ok) await parseError(response)
  return ((await response.json()) as { url: string }).url
}

// ---- admin ----

export interface AdminNode {
  id: string
  name: string
  quotaBytes: number | null
  usedBytes: number
  usageScannedAt: string | null
  lastSeenAt: string | null
  online: boolean
  info: {
    version: string
    root: string
    platform: string
    totalBytes: number | null
    freeBytes: number | null
    filesystems: { path: string; filesystem: string; totalBytes: number; freeBytes: number }[]
    readOnly: boolean
  } | null
}

export interface AdminShare {
  id: string
  userId: string
  userEmail: string
  userDisplayName: string
  nodeId: string
  nodeName: string
  path: string
  label: string
  permission: 'read' | 'write'
  quotaBytes: number | null
  usedBytes: number
}

export interface AuditEntry {
  id: string
  at: string
  actor: string | null
  action: string
  path: string
  detail: string
  bytes: number | null
  succeeded: boolean
}

export interface AgentInstall {
  image: string
  hubUrl: string
}

export const admin = {
  agentInstall: () => api<AgentInstall>('/api/admin/agent-install'),
  users: () =>
    api<{ users: (ApiUser & { disabled: boolean })[] }>('/api/admin/users').then((r) => r.users),
  createUser: (body: {
    email: string
    displayName: string
    password: string
    role: 'admin' | 'user'
  }) => api<{ user: ApiUser }>('/api/admin/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: string, body: Record<string, unknown>) =>
    api<{ user: ApiUser }>(`/api/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteUser: (id: string) => api<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' }),

  nodes: () => api<{ nodes: AdminNode[] }>('/api/admin/nodes').then((r) => r.nodes),
  createNode: (body: { name: string; quotaBytes: number | null }) =>
    api<{ node: { id: string; name: string }; token: string }>('/api/admin/nodes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateNode: (id: string, body: Record<string, unknown>) =>
    api<unknown>(`/api/admin/nodes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  rotateNodeToken: (id: string) =>
    api<{ token: string }>(`/api/admin/nodes/${id}/token`, { method: 'POST' }),
  scanNode: (id: string) =>
    api<{ usedBytes: number }>(`/api/admin/nodes/${id}/scan`, { method: 'POST' }),
  deleteNode: (id: string) => api<{ ok: true }>(`/api/admin/nodes/${id}`, { method: 'DELETE' }),

  shares: () => api<{ shares: AdminShare[] }>('/api/admin/shares').then((r) => r.shares),
  createShare: (body: {
    userId: string
    nodeId: string
    path: string
    label: string
    permission: 'read' | 'write'
    quotaBytes: number | null
  }) => api<unknown>('/api/admin/shares', { method: 'POST', body: JSON.stringify(body) }),
  updateShare: (id: string, body: Record<string, unknown>) =>
    api<unknown>(`/api/admin/shares/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteShare: (id: string) => api<{ ok: true }>(`/api/admin/shares/${id}`, { method: 'DELETE' }),

  audit: () => api<{ entries: AuditEntry[] }>('/api/admin/audit').then((r) => r.entries),
}
